// eslint-disable-next-line no-restricted-imports
import i18next from 'i18next'
import HttpBackend from 'i18next-http-backend'
import {TranslationNamespaces, ColonistI18n, I18n, TranslationKeys, TranslationOptions} from '@colonist/i18n'
import {initTranslation} from './ui_utils/DomUtils'
import {CountryCode} from '@colonist/leaderboard-types'
import {UIUtilsEjsVariables} from './UIUtilsEjsVariables'
import {UIImagePathController} from './UIEnums.ts'
import {ClientUserState} from './ClientUserState'
import {StorageWrapper} from './StorageWrapper'

export namespace ClientI18nInitiation {
    const i18n = i18next

    export async function init() {
        initTranslation((key: TranslationKeys, options?: TranslationOptions) => clientI18n.t(key, options))

        await i18n.use(HttpBackend).init({
            lng: UIUtilsEjsVariables.language,
            fallbackLng: I18n.defaultLanguage,
            ns: TranslationNamespaces.Strings,
            backend: {
                loadPath: (lng, ns) => UIImagePathController.getLocalePath(`${lng}/${ns}.json`, UIUtilsEjsVariables.isUserOnDiscord),
            },
            defaultNS: TranslationNamespaces.Strings,
            interpolation: {
                skipOnVariables: false,
            },
        })

        ClientUserState.registerChangeListener((userState) => {
            if(userState == undefined) return
            if(ClientUserState.isStale()) return

            const userLanguage = userState.language ?? I18n.defaultLanguage

            if(StorageWrapper.getLanguage() != userLanguage) {
                StorageWrapper.setLanguage(userLanguage)
            }

            if(UIUtilsEjsVariables.language == userLanguage) return
            if(UIUtilsEjsVariables.language != I18n.defaultLanguage) return

            window.location.pathname = replaceLanguageInPath(window.location.pathname, userLanguage)
        })
    }
}

export const clientI18n = new ColonistI18n(i18next as any)

export class IntlColonistWrapper {
    private readonly defaultLocale: string

    constructor(locale: string) {
        this.defaultLocale = locale
    }

    getLanguageName(code: string, overrideLocale?: string): string | undefined {
        const locale = overrideLocale ?? this.defaultLocale
        try {
            return new Intl.DisplayNames(locale, {type: 'language'}).of(code)
        } catch(e) {
            return I18n.languageEnglishNames[code]
        }
    }

    getCountryName(countryCode: CountryCode, overrideLocale?: string): string | undefined {
        const locale = overrideLocale ?? this.defaultLocale
        try {
            return new Intl.DisplayNames(locale, {type: 'region'}).of(countryCode)
        } catch(e) {
            return undefined
        }
    }

    getLanguagePathPrefix() {
        return this.defaultLocale === I18n.defaultLanguage ? '' : `/${this.defaultLocale}`
    }
}

export const clientIntl = new IntlColonistWrapper(UIUtilsEjsVariables.language)

export function replaceLanguageInPath(path: string, newLanguage: string): string {
    const pathParts = path.split('/')
    const newPathParts: string[] = []

    if(pathParts[0] == '') {
        newPathParts.push(pathParts.shift() ?? '')
    }

    if(pathParts[0] == '.proxy') { // discord
        newPathParts.push(pathParts.shift() ?? '')
    }

    if(pathParts[0]?.match(/^[a-z]{2}(-[a-z]{2})?$/i)) {
        pathParts.shift() // remove current language
    }
    if(newLanguage != I18n.defaultLanguage) newPathParts.push(newLanguage)

    newPathParts.push(...pathParts)

    if(newPathParts[newPathParts.length - 1] == '') {
        newPathParts.pop() // create language urls that are the same as the canonical url. /ca, not /ca/
    }

    return newPathParts.join('/')
}
